import { Link } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { MapPin, Truck, Store, ShoppingCart } from "lucide-react";

function money(cents) {
  const dollars = (cents / 100).toFixed(2);
  return `$${dollars}`;
}

export default function ProductCard({ product, showAddToCart = false, onAddToCart }) {
  const seller = product.seller;
  const href = seller ? `/shop/${seller.username}/p/${product.slug}` : `/marketplace`;
  const cover = product.images?.[0];
  const hasSale = product.sale_price_cents != null && product.sale_price_cents !== product.price_cents;
  const soldOut = !!product.sold_out;
  const isCustomOnly = product.is_custom_order && product.price_cents === 0;
  const canAddToCart = showAddToCart && !soldOut && !isCustomOnly && typeof onAddToCart === "function";

  const handleAddClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onAddToCart?.(1);
  };

  return (
    <Link to={href} className="cp-card overflow-hidden group flex flex-col relative" data-testid={`product-card-${product.id}`}>
      <div className="aspect-[4/3] bg-[#F9EFE6] overflow-hidden relative">
        {cover ? (
          <img src={fileUrl(cover)} alt="" className={`w-full h-full object-cover transition-transform duration-500 ${soldOut ? "grayscale opacity-70" : "group-hover:scale-[1.03]"}`} />
        ) : (
          <div className="w-full h-full grid place-items-center text-[#857163]">
            <Store className="w-10 h-10" />
          </div>
        )}
        {hasSale && !soldOut && (
          <div className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#FFD07F] text-[#B26A00] shadow-sm" data-testid="sale-badge">
            {product.discount_pct ? `${product.discount_pct}% off` : `${money(product.discount_cents || 0)} off`}
          </div>
        )}
        {soldOut && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <span className="text-white font-heading text-2xl uppercase tracking-widest px-4 py-1 rounded-lg bg-[#C2185B]/90 shadow-lg" data-testid="soldout-overlay">
              Sold out
            </span>
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="font-heading text-lg text-[#3B2C24] leading-tight truncate">{product.title}</div>
          <div className="font-semibold text-sm shrink-0 text-right">
            {isCustomOnly ? (
              <span className="text-[#FF7E67]">Custom</span>
            ) : hasSale ? (
              <>
                <span className="text-[#C2185B]">{money(product.sale_price_cents)}</span>
                <div className="text-[#857163] line-through text-[10px] font-normal">{money(product.price_cents)}</div>
              </>
            ) : (
              <span className="text-[#FF7E67]">{money(product.price_cents)}</span>
            )}
          </div>
        </div>
        {seller && (
          <div className="text-xs text-[#857163] truncate">
            by <span className="text-[#3B2C24] font-medium">{seller.business_name}</span>
            {seller.city && <> · {seller.city}</>}
          </div>
        )}
        <div className="flex-1" />
        <div className="mt-3 flex items-center gap-1.5 flex-wrap text-[10px] text-[#857163]">
          {product.fulfillment_pickup && <span className="cp-tag" style={{ background: "rgba(142,172,80,0.15)" }}><MapPin className="w-3 h-3" /> Pickup</span>}
          {product.fulfillment_shipping && <span className="cp-tag" style={{ background: "rgba(255,208,127,0.28)" }}><Truck className="w-3 h-3" /> Ships</span>}
          {product.is_custom_order && <span className="cp-tag" style={{ background: "rgba(255,126,103,0.15)" }}>Custom orders</span>}
          {typeof product.available_quantity === "number" && !soldOut && (
            <span
              className="cp-tag font-semibold"
              style={{
                background: product.low_stock_threshold != null && product.available_quantity <= product.low_stock_threshold
                  ? "rgba(244,168,59,0.22)"
                  : "rgba(142,172,80,0.15)",
                color: product.low_stock_threshold != null && product.available_quantity <= product.low_stock_threshold
                  ? "#B26A00"
                  : "#4C7A2E",
              }}
              data-testid={`stock-pill-${product.id}`}
            >
              {product.available_quantity} in stock
            </span>
          )}
        </div>
        {canAddToCart && (
          <button
            onClick={handleAddClick}
            className="mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-white font-semibold text-xs hover:opacity-90 transition"
            style={{ background: "var(--shop-accent, #FF7E67)" }}
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart className="w-3.5 h-3.5" /> Add to cart
          </button>
        )}
      </div>
    </Link>
  );
}
